import { useMutation } from "@apollo/client";
import { useState } from "react";
import { AttendeeRole, OnlineConference } from "types/graphql-schema";

import createOrUpdateMeetingInExternalCalendarMutation from "@apps/meeting-dialog/graphql/create-or-update-meeting-in-external-calendar-mutation";
import MeetingDialog from "@apps/meeting-dialog/meeting-dialog";
import useLabel from "@apps/use-label/use-label";
import { currentUserVar, errorNotificationVar } from "@cache/cache";
import { useLink } from "@components/link/link-helpers";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { TopicflowIconType } from "@components/topicflow-icon/topicflow-icon-helpers";
import { meetingEditType } from "@helpers/constants";

import getProfileOneononesQuery from "./graphql/get-profile-oneonones-query";

const GetStartedCard = ({
  icon,
  title,
  body,
  onClick,
  disabled = false,
}: {
  icon: TopicflowIconType;
  title: string;
  body: string;
  onClick: () => void;
  disabled?: boolean;
}) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    className="flex-1 flex flex-col items-center text-center bg-white border border-gray-200 p-6 @4xl:p-9 rounded-[10px] hover:border-blue-400 hover:shadow-sm transition disabled:opacity-50 disabled:cursor-not-allowed"
  >
    <TopicflowIcon
      type={icon}
      className="w-6 h-6 @4xl:w-8 @4xl:h-8 text-blue-500 mb-4"
    />
    <div className="text-base font-medium text-gray-800 mb-1">{title}</div>
    <div className="tf-body-sm">{body}</div>
  </button>
);

const displayName = (firstName: string | null | undefined, email: string) =>
  firstName?.trim() || email;

const ProfileOneononeEmptyState = ({
  profileId,
  profileName,
  profileFirstName,
  profileEmail,
}: {
  profileId: number;
  profileName: string;
  profileFirstName: string | null | undefined;
  profileEmail: string;
}) => {
  const label = useLabel();
  const link = useLink();
  const currentUser = currentUserVar();
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);

  const currentUserDisplay = displayName(
    currentUser.firstName,
    currentUser.email,
  );
  const profileDisplay = displayName(profileFirstName, profileEmail);
  const meetingTitle = `${label("1-on-1")}: ${currentUserDisplay} - ${profileDisplay}`;

  const formOptions = {
    attendees: [
      { id: currentUser.id, name: currentUser.name, email: currentUser.email },
      { id: profileId, name: profileName, email: profileEmail },
    ],
    isFormalOneonone: true,
    title: meetingTitle,
  };

  const [createDraftMeeting, { loading: creatingDraft }] = useMutation(
    createOrUpdateMeetingInExternalCalendarMutation,
    {
      refetchQueries: [getProfileOneononesQuery],
      onCompleted: ({ createOrUpdateMeetingInExternalCalendar }) => {
        const meeting = createOrUpdateMeetingInExternalCalendar?.meeting;
        if (meeting?.meetingGroup) {
          link.redirect(`/profile/${profileId}/oneonone/${meeting.id}`);
        }
      },
      onError: () => {
        errorNotificationVar({
          title: `Could not create draft ${label("1-on-1")}`,
          timeout: 5000,
        });
      },
    },
  );

  const handleCreateDraft = () => {
    createDraftMeeting({
      variables: {
        attendees: [
          { email: currentUser.email, role: AttendeeRole.Required },
          { email: profileEmail, role: AttendeeRole.Required },
        ],
        onlineConference: OnlineConference.Native,
        editType: meetingEditType.singleEvent,
        publish: false,
        title: meetingTitle,
        isFormalOneonone: true,
        addTranscriptionBotToMeetingsByDefault: true,
      },
    });
  };

  return (
    <>
      <div className="min-h-[70vh] flex flex-col items-center justify-center gap-12 py-12 px-6">
        <div className="tf-header-2xl text-center">
          Let&apos;s get started with your {label("1-on-1")}.
        </div>
        <div className="w-full max-w-5xl @container">
          <div className="flex flex-col @4xl:flex-row gap-3 @4xl:gap-6 max-w-[500px] @4xl:max-w-none mx-auto">
            <GetStartedCard
              icon="file04"
              title="Create empty meeting"
              body="Start from a blank slate and capture notes right away."
              onClick={handleCreateDraft}
              disabled={creatingDraft}
            />
            <GetStartedCard
              icon="calendarPlus"
              title={`Schedule recurring ${label("1-on-1")}`}
              body="Set a regular cadence so you never miss a check-in."
              onClick={() => setShowScheduleDialog(true)}
            />
          </div>
        </div>
      </div>

      {showScheduleDialog && (
        <MeetingDialog
          formOptions={formOptions}
          refetchQueries={[getProfileOneononesQuery]}
          onClose={() => setShowScheduleDialog(false)}
          onSaved={() => setShowScheduleDialog(false)}
        />
      )}
    </>
  );
};

export default ProfileOneononeEmptyState;
